import Backbone from 'backbone';
import renderContainer from '../toolbox/renderContainer';
import parseQueryString from '../toolbox/parseQueryString';
import isAuthenticated from '../toolbox/isAuthenticated';

// containers
import LandingPageContainer from '../containers/landing/LandingPageContainer';
import DashboardContainer from '../containers/dashboard/DashboardContainer';
import MetadataContainer from '../containers/metadata/MetadataContainer';
import ActivityLogContainer from '../containers/activity/ActivityLogContainer';
import TeamContainer from '../containers/team/TeamContainer';

// login module
import LoginContainer from '../containers/login/LoginContainer';
import LogoutContainer from '../containers/login/LogoutContainer';
import SignUpContainer from '../containers/login/SignUpContainer';
import ForgotPasswordContainer from '../containers/login/ForgotPasswordContainer';

// patient
import ConnectedPatientListContainer from '../containers/patient/PatientListContainer';
import ConnectedPatientDetailContainer from '../containers/patient/PatientDetailContainer';
import CreatePatientContainer from '../containers/patient/CreatePatientContainer';
import ConnectedReferPatientContainer from '../containers/patient/ReferPatientContainer';
import CreateWoundContainer from '../containers/patient/CreateWoundContainer';
import CreateAssessmentContainer from '../containers/patient/CreateAssessmentContainer';
import UploadWoundPhotoContainer from '../containers/patient/UploadWoundPhotoContainer';

// profile
import ProfileContainer from '../containers/profile/ProfileContainer';
import EditProfileContainer from '../containers/profile/EditProfileContainer';
import EditPasswordContainer from '../containers/profile/EditPasswordContainer';

// record
import ConnectedRecordListContainer from '../containers/record/RecordListContainer';
import ConnectedRecordDetailContainer from '../containers/record/RecordDetailContainer';

// testimonial
import AdminTestimonialContainer from '../containers/testimonial/AdminTestimonialContainer';
import PublicTestimonialContainer from '../containers/testimonial/PublicTestimonialContainer';

// fpp
import FPPContainer from '../containers/fpp/FPPContainer';
import FPPAddAssessment from '../containers/fpp/AddAssessmentContainer';
import FPPAssessmentViewContainer from '../containers/fpp/FPPAssessmentViewContainer';
import ExerciseContainer from '../containers/fpp/ExerciseContainer';

// subscription
import SubscriptionContainer from '../containers/subscription/SubscriptionContainer';

// messaging
// import MessagingMainPage from '../containers/messaging/MessagingMainPage';
import VideoChatContainer from '../containers/messaging/VideoChatContainer';
import MessagingContainer from '../containers/messaging/MessagingContainer';

// static pages
import ConnectWithUs from '../components/static/ConnectWithUs';
import Notice from '../components/static/Notice';
import PrivacyPolicy from '../components/static/PrivacyPolicy';
import ProductIntro from '../components/static/ProductIntro';
import UserAgreement from '../components/static/UserAgreement';
import FAQ from '../components/static/FAQ';
import PrivacyStatement from '../components/static/PrivacyStatement';

const MAIN = 'main'; // the dom element id to render containers

export default Backbone.Router.extend({
  routes: {
    '': 'index',
    metadata: 'metadata',
    'activity-logs': 'activityLogs',
    team: 'team',
    dashboard: 'dashboard',

    // login and auth related routes
    login: 'login',
    logout: 'logout',
    signup: 'signup',
    forgot: 'forgot',

    // patient
    'medical-records': 'patientList',
    'medical-records/:id(?section=:section)': 'patientDetail',
    'medical-records-create': 'createPatient',
    'medical-records/:id/refer': 'referPatient',
    'medical-records/:id/wounds-create': 'createWound',
    'medical-records/:medicalRecordId/wounds/:woundId/assessment-create': 'createAssessment',
    'medical-records/:medicalRecordId/wounds/:woundId/assessment/:assessmentId/photo-upload': 'uploadPhoto',
    'medical-records/:medicalRecordId/fpp/:fppId/assessment/:assessmentId/list': 'fpp', // list fpp assessments
    'medical-records/:medicalRecordId/fpp/:fppId/assessment/:assessmentId': 'fppAssessmentView', // show fpp assessment detail
    'medical-records/:medicalRecordId/fpp/:fppId/add': 'fppAdd', // add fpp assessment


    // file
    file: 'fileUpload',

    // fpp
    fpp: 'fpp', // for which patient
    'fpp/add': 'fppAdd', // for which patient
    'fpp/exercise': 'fppExercise',

    // subscription
    subscription: 'subscription',

    // profile
    profile: 'profile',
    'profile/edit': 'editProfile',
    'profile/edit/password': 'editPassword',

    // records
    records: 'recordList',
    'records/:id': 'recordDetail',

    // testimonials
    testimonials: 'testimonials',
    'admin-testimonials': 'adminTestimonials',

    // team messaging
    messaging: 'messaging',
    'videoChat/:currentChannelId': 'videoChat',

    // static pages
    notice: 'notice', // notice page for invalid invitation
    'frequently-asked-questions': 'faq',
    'privacy-policy': 'privacy',
    'product-intro': 'intro',
    'connect-with-us': 'connectWithUs',
    'user-agreement': 'userAgreement',
    'privacy-statement': 'privacyStatement',
    '*notFound': 'notFound',

  },

  initialize: function initialize(options) {
    /**
     *  NOTE: Most pages built earlier than pixel-perfect is
     *  designed based on boostrap's responsive "container" style.
     *  However, the landing page in pixel-perfect need full-width background,
     *  which cannot use "container".
     *  Thus, we decide to assign containers other than landing pages the "container" style.
     */
    this.render = (Container, ownProps, isLandingPage = false) => {
      renderContainer(Container, options.store, this, MAIN, ownProps, isLandingPage);
    };
  },

  execute: function execute(callback, args) {
    const { pathname, origin } = window.location;

    if (!isAuthenticated()
      && pathname !== '/login'
      && pathname !== '/forgot'
      && pathname !== '/signup'
      && pathname !== '/'
      && pathname !== '/connect-with-us'
      && pathname !== '/testimonials'
      && pathname !== '/product-intro'
      && pathname !== '/user-agreement'
      && pathname !== '/privacy-policy'
      && pathname !== '/subscription'
      && pathname !== '/frequently-asked-questions'
      && pathname !== '/privacy-statement'
    ) {
      window.location.href = `${origin}/login`;
      return this.login();
    }

    if (isAuthenticated() && pathname === '/') {
      window.location.href = `${origin}/dashboard`;
      return this.dashboard();
    }

    if (callback) {
      return callback.apply(this, args);
    }
    return false;
  },

  index: function index() {
    this.render(LandingPageContainer, null, true);
  },

  dashboard: function dashboard() {
    this.render(DashboardContainer);
  },

  metadata: function metadata() {
    this.render(MetadataContainer);
  },

  activityLogs: function activityLogs() {
    this.render(ActivityLogContainer);
  },

  team: function team() {
    this.render(TeamContainer);
  },

  /**
   * login routes
   */

  login: function login() {
    this.render(LoginContainer);
  },

  logout: function logout() {
    this.render(LogoutContainer);
  },

  signup: function signup(queryString) {
    const { token, email } = parseQueryString(queryString || '');
    this.render(SignUpContainer, { token, email });
  },

  forgot: function forgot() {
    this.render(ForgotPasswordContainer);
  },

  /**
   * Patient module
   */
  patientList: function patientList() {
    this.render(ConnectedPatientListContainer);
  },

  patientDetail: function patientDetail(id, section) {
    this.render(ConnectedPatientDetailContainer, { medicalRecordId: id, targetSection: section });
  },

  createPatient: function createPatient() {
    this.render(CreatePatientContainer);
  },

  referPatient: function referPatient(id) {
    this.render(ConnectedReferPatientContainer, { id: parseInt(id, 10) });
  },

  createWound: function createWound(id) {
    this.render(CreateWoundContainer, { medicalRecordId: id });
  },

  createAssessment: function createAssessment(medicalRecordId, woundId) {
    this.render(CreateAssessmentContainer, {
      medicalRecordId: parseInt(medicalRecordId, 10),
      woundId: parseInt(woundId, 10),
    });
  },

  uploadPhoto: function uploadPhoto(medicalRecordId, woundId, assessmentId) {
    this.render(UploadWoundPhotoContainer, { medicalRecordId, woundId, assessmentId });
  },

  /**
   * profile routes
   */
  profile: function profile() {
    this.render(ProfileContainer);
  },

  editProfile: function editProfile() {
    this.render(EditProfileContainer);
  },

  editPassword: function editPassword() {
    this.render(EditPasswordContainer);
  },

  /**
   * record module (for patient to view their own medical records)
   */
  recordList: function recordList() {
    this.render(ConnectedRecordListContainer);
  },

  recordDetail: function recordDetail(id) {
    this.render(ConnectedRecordDetailContainer, { medicalRecordId: id });
  },

  /**
   * testimonial system
   */

  testimonials: function testimonials() {
    this.render(PublicTestimonialContainer, { showAll: true });
  },

  adminTestimonials: function adminTestimonials() {
    this.render(AdminTestimonialContainer);
  },

  /**
   * fpp
   */

  fpp: function fpp() {
    this.render(FPPContainer);
  },

  fppAdd: function fppAdd(medicalRecordId, surveyId) {
    this.render(FPPAddAssessment, { medicalRecordId, surveyId });
  },

  fppAssessmentView: function fppAssessmentView(medicalRecordId, surveyId, assessmentId) {
    this.render(FPPAssessmentViewContainer, { medicalRecordId, surveyId, assessmentId });
  },

  fppExercise: function fppExercise() {
    this.render(ExerciseContainer);
  },

  /**
   * subscription
   */
  subscription: function subscription() {
    this.render(SubscriptionContainer, {});
  },

  /**
   * team messaging module
   */
  messaging: function Messaging() {
    this.render(MessagingContainer, {}, true);
  },

  videoChat: function videoChat(currentChannelId) {
    this.render(VideoChatContainer, { currentChannelId });
  },
  /**
   * static pages
   */

  connectWithUs: function connectWithUs() {
    this.render(ConnectWithUs);
  },

  notice: function notice() {
    this.render(Notice);
  },

  privacy: function privacy() {
    this.render(PrivacyPolicy);
  },

  intro: function intro() {
    this.render(ProductIntro);
  },

  userAgreement: function userAgreement() {
    this.render(UserAgreement);
  },

  faq: function faq() {
    this.render(FAQ);
  },

  privacyStatement: function privacyStatement() {
    this.render(PrivacyStatement);
  },

  /**
   * 404
   */
  notFound: function notFound() {
    // TODO: probably should use 404 page in this case
    this.navigate('/', { trigger: true });
  },
});



// WEBPACK FOOTER //
// ./src/routers/Router.js